// src/pages/Admin/Users/AddUserModal.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaEnvelope,
  FaIdBadge,
  FaInfoCircle,
  FaPlus,
  FaSave,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUser,
  FaUserCog,
  FaUserGraduate,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { addUserByAdmin } from "../../../services/userServices";

const defaultUserData = () => ({
  nom: "",
  prenom: "",
  email: "",
  role_id: "",
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const ROLE_OPTIONS = [
  {
    id: 1,
    label: "Administrateur",
    description: "Accès complet à la gestion du club.",
    icon: FaUserCog,
    tone: "from-rose-500 to-orange-500",
  },
  {
    id: 2,
    label: "Utilisateur",
    description: "Accès adhérent classique.",
    icon: FaUser,
    tone: "from-blue-500 to-indigo-500",
  },
  {
    id: 3,
    label: "Enseignant",
    description: "Accès aux outils pédagogiques des enseignants.",
    icon: FaUserGraduate,
    tone: "from-emerald-500 to-teal-500",
  },
];

const roleLabel = (roleId) => {
  const role = ROLE_OPTIONS.find((item) => Number(item.id) === Number(roleId));
  return role?.label || "Rôle non défini";
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function PreviewLine({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

function UserPreviewCard({ userData }) {
  const fullName = `${userData.prenom || ""} ${userData.nom || ""}`.trim();
  const initials = `${String(userData.prenom || "").charAt(0)}${String(
    userData.nom || ""
  ).charAt(0)}`.toUpperCase();

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaUser className="text-xl" />
          </div>

          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Nouveau compte
            </p>
            <p className="truncate text-lg font-bold">
              {fullName || "Utilisateur"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 text-center">
        <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-[2rem] bg-gradient-to-br from-slate-100 to-slate-200 text-4xl font-bold text-slate-500 shadow-inner ring-4 ring-white dark:from-slate-800 dark:to-slate-900 dark:text-slate-300 dark:ring-slate-900">
          {initials || <FaUser />}
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
          {fullName || "Nom prénom"}
        </h3>

        <span className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
          {roleLabel(userData.role_id)}
        </span>

        <div className="mt-4 grid grid-cols-1 gap-2 text-left">
          <PreviewLine label="Email" value={userData.email || "—"} />
          <PreviewLine label="Rôle" value={roleLabel(userData.role_id)} />
        </div>
      </div>
    </section>
  );
}

function RoleSelector({ value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      {ROLE_OPTIONS.map((role) => {
        const Icon = role.icon;
        const active = Number(value) === Number(role.id);

        return (
          <button
            key={role.id}
            type="button"
            onClick={() => onChange(role.id)}
            disabled={disabled}
            className={`group flex min-h-[82px] items-center gap-4 rounded-3xl p-4 text-left ring-1 transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${
              active
                ? "bg-violet-50 ring-violet-200 dark:bg-violet-900/20 dark:ring-violet-800/70"
                : "bg-slate-50 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:ring-slate-800 dark:hover:bg-slate-900"
            }`}
          >
            <span
              className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${role.tone} text-white shadow-sm transition group-hover:scale-105`}
            >
              <Icon />
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                {role.label}
                {active && (
                  <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold text-white">
                    choisi
                  </span>
                )}
              </span>

              <span className="mt-1 block text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {role.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function AddUserModal({ isOpen, onClose, refreshList }) {
  const dialogRef = useRef(null);

  const [userData, setUserData] = useState(defaultUserData());
  const [saving, setSaving] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      !saving &&
      String(userData.nom || "").trim().length > 0 &&
      String(userData.prenom || "").trim().length > 0 &&
      EMAIL_RE.test(String(userData.email || "").trim()) &&
      !!userData.role_id
    );
  }, [saving, userData]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [onClose, saving]);

  useEffect(() => {
    if (!isOpen) return;

    setUserData(defaultUserData());
    setSaving(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setUserData((previous) => ({
      ...previous,
      [name]: name === "role_id" ? (value ? Number(value) : "") : value,
    }));
  };

  const handleRoleChange = (roleId) => {
    setUserData((previous) => ({
      ...previous,
      role_id: Number(roleId),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cleanPayload = {
      nom: String(userData.nom || "").trim(),
      prenom: String(userData.prenom || "").trim(),
      email: String(userData.email || "").trim().toLowerCase(),
      role_id: Number(userData.role_id),
    };

    if (!cleanPayload.nom) {
      toast.warn("Le nom est obligatoire.");
      return;
    }

    if (!cleanPayload.prenom) {
      toast.warn("Le prénom est obligatoire.");
      return;
    }

    if (!EMAIL_RE.test(cleanPayload.email)) {
      toast.warn("Merci de saisir un email valide.");
      return;
    }

    if (!cleanPayload.role_id) {
      toast.warn("Merci de sélectionner un rôle.");
      return;
    }

    try {
      setSaving(true);

      await addUserByAdmin(cleanPayload);

      toast.success("Utilisateur créé avec succès.");

      refreshList?.();
      onClose?.();
      setUserData(defaultUserData());
    } catch (error) {
      console.error("Erreur création utilisateur:", error);
      toast.error("Erreur lors de la création de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter un utilisateur"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Gestion utilisateur
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Ajouter un utilisateur
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Créez un nouveau compte avec son identité, son email et son rôle
                dans l’application.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="add-user-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <UserPreviewCard userData={userData} />

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Vérification rapide avant création."
                >
                  <div className="space-y-3">
                    <PreviewLine
                      label="Nom"
                      value={userData.nom?.toUpperCase() || "—"}
                    />
                    <PreviewLine
                      label="Prénom"
                      value={userData.prenom || "—"}
                    />
                    <PreviewLine
                      label="Rôle"
                      value={roleLabel(userData.role_id)}
                    />
                  </div>

                  <div className="mt-4 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:ring-blue-900">
                    Une fois le compte créé, vous pourrez modifier la photo de
                    l’utilisateur depuis la fiche d’édition.
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaIdBadge}
                  title="Identité"
                  description="Les champs nom, prénom et email sont obligatoires."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Nom <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="nom"
                          value={userData.nom}
                          onChange={handleChange}
                          required
                          disabled={saving}
                          placeholder="Ex : Dupont"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Prénom <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaUser className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          name="prenom"
                          value={userData.prenom}
                          onChange={handleChange}
                          required
                          disabled={saving}
                          placeholder="Ex : Camille"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className={labelClass}>
                        Email <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="email"
                          name="email"
                          value={userData.email}
                          onChange={handleChange}
                          required
                          disabled={saving}
                          placeholder="exemple@email.fr"
                          className={`${inputClass} pl-9`}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaShieldAlt}
                  title="Rôle utilisateur"
                  description="Choisissez le niveau d’accès du nouveau compte."
                >
                  <div className="space-y-4">
                    <select
                      name="role_id"
                      value={userData.role_id}
                      onChange={handleChange}
                      required
                      disabled={saving}
                      className={inputClass}
                    >
                      <option value="">Sélectionner un rôle</option>
                      <option value="1">Administrateur</option>
                      <option value="2">Utilisateur</option>
                      <option value="3">Enseignant</option>
                    </select>

                    <RoleSelector
                      value={userData.role_id}
                      onChange={handleRoleChange}
                      disabled={saving}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaPlus}
                  title="Création du compte"
                  description="Le compte sera ajouté à la liste des utilisateurs."
                >
                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                    <p className="text-sm font-bold text-slate-800 dark:text-white">
                      Prêt à créer : {canSubmit ? "oui" : "non"}
                    </p>

                    <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      Vérifiez que l’email est correct avant validation. Selon
                      votre backend, l’utilisateur pourra ensuite recevoir ses
                      identifiants ou réinitialiser son mot de passe.
                    </p>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Fermer
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Création…
                  </>
                ) : (
                  <>
                    <FaSave />
                    Créer l’utilisateur
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .add-user-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .add-user-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .add-user-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .add-user-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}